import type { ReactElement } from 'react'

import { cn } from '@/lib/cn'
import { inteiro, percentual } from '@/lib/formato'

export interface FunilVisualProps {
  passos: { rotulo: string; qtd: number; pctDoAnterior: number | null }[]
  /** Escala de telão: tipografia maior pro canvas de 2500px da apresentação. */
  telao?: boolean
}

/**
 * Funil em forma de funil: trapézios centralizados afunilando de cima pra
 * baixo. O afunilamento é FIXO (100% → 40%): os volumes reais são tão
 * assimétricos (milhares → dezenas) que largura proporcional sumiria com os
 * passos finais — a forma comunica a ordem, os números carregam o dado.
 */
export function FunilVisual({ passos, telao = false }: FunilVisualProps): ReactElement {
  const degrau = 60 / Math.max(1, passos.length - 1)
  const largura = (i: number): number => 100 - i * degrau

  return (
    <ol className={cn('flex flex-col justify-center gap-1', telao && 'h-full')}>
      {passos.map((passo, i) => {
        const topo = largura(i)
        const base = i + 1 < passos.length ? largura(i + 1) : largura(i) * 0.8
        const alfa = Math.max(0.55, 0.92 - i * 0.09)
        return (
          <li key={passo.rotulo} className={cn('relative', telao ? 'h-[76px]' : 'h-16')}>
            <div
              data-testid={`degrau-${i}`}
              aria-hidden
              className="absolute inset-0"
              style={{
                background: `linear-gradient(180deg, hsl(var(--primary) / ${alfa}), hsl(var(--primary) / ${alfa - 0.12}))`,
                clipPath: `polygon(${(100 - topo) / 2}% 0, ${(100 + topo) / 2}% 0, ${(100 + base) / 2}% 100%, ${(100 - base) / 2}% 100%)`,
              }}
            />
            <div
              data-testid={`conteudo-${i}`}
              className="text-primary-foreground relative mx-auto flex h-full flex-col items-center justify-center leading-tight"
              style={{ width: `${topo}%` }}
            >
              <span className={cn('font-serif', telao ? 'text-3xl' : 'text-xl')}>
                {inteiro(passo.qtd)}
              </span>
              <span className={cn('max-w-full truncate font-medium', telao ? 'text-base' : 'text-xs')}>
                {passo.rotulo}
                {passo.pctDoAnterior !== null
                  ? ` · ${percentual(passo.pctDoAnterior / 100)} do anterior`
                  : ''}
              </span>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
